import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Telescope, RefreshCw, ArrowUpRight, Sparkles, Target, Rocket, Loader2, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

export default function Scouting() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [reserving, setReserving] = useState(null); // {idx, mode}
  const [launchDefaults, setLaunchDefaults] = useState(null);
  const [confirmLaunch, setConfirmLaunch] = useState(null); // {item, idx}
  const nav = useNavigate();

  const [cacheInfo, setCacheInfo] = useState(null); // { cached: bool, age_min: int }

  const load = async (force = false) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/ai/scouting${force ? "?force_refresh=true" : ""}`, { timeout: 90000 });
      setItems(data.items || []);
      setCacheInfo({ cached: !!data.cached, age_min: data.cache_age_minutes ?? 0 });
    } catch (e) {
      toast.error(e?.code === "ECONNABORTED"
        ? "Scouting timed out (Claude + Google Trends can be slow). Try again in a moment."
        : "Scouting failed");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    // Both admins and buyers need to know whether defaults are set
    // (the button is disabled otherwise).
    api.get("/workspace/launch-defaults").then(({ data }) => setLaunchDefaults(data)).catch(() => {});
  }, []);

  const pickIdea = (item) => {
    nav("/campaigns/new", { state: { prefill: item } });
  };

  const reserveFirst = async (item, idx) => {
    setReserving({ idx, mode: "reserve" });
    try {
      const { data } = await api.post("/scouting/reserve", item);
      const promoteNote = data.promoted_keyword
        ? `Brief ready · "${data.promoted_keyword}" ★ promoted for next Matrix launch`
        : "Brief ready · review and push";
      nav("/campaigns/new", { state: { brief: data.brief, note: promoteNote } });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reserve failed — check LLM balance");
    } finally { setReserving(null); }
  };

  const autoLaunch = async () => {
    if (!confirmLaunch) return;
    const { item, idx } = confirmLaunch;
    setConfirmLaunch(null);
    setReserving({ idx, mode: "launch" });
    try {
      const { data } = await api.post("/scouting/reserve-and-launch", item);
      toast.success(
        `🚀 Campaign live (paused) · ${data.promoted_keyword ? `★ "${data.promoted_keyword}"` : ""} · fb_id ${data.campaign?.fb_campaign_id}`,
        { duration: 8000 }
      );
      // Refresh cap remaining
      try {
        const { data: ld } = await api.get("/workspace/launch-defaults");
        setLaunchDefaults(ld);
      } catch (e) { console.error("launch-defaults refresh failed", e); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-launch failed", { duration: 8000 });
    } finally { setReserving(null); }
  };

  return (
    <div className="p-6 max-w-[1600px]" data-testid="scouting-page" style={{ fontFamily: "Manrope" }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Telescope className="w-6 h-6 text-blue-600" /> Campaign Scouting
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-indigo-500/10 text-indigo-700 border border-indigo-500/20 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> AI-SGE frontrunning
            </span>
          </h1>
          <p className="text-xs text-slate-500 mt-1 font-mono">
            Trending search topics Google's AI Overviews is surfacing — zero-competition RPC windows before the paid-search auction catches up
          </p>
        </div>
        <div className="flex items-center gap-2">
          {launchDefaults?.configured && launchDefaults?.daily_spend_cap_usd > 0 && (
            <div
              data-testid="autolaunch-cap-meter"
              className="flex items-center gap-1.5 bg-red-50 border border-red-200 rounded px-2.5 py-1 text-[11px] font-mono"
              title="Daily auto-launch spend cap — resets at midnight UTC"
            >
              <Zap className="w-3 h-3 text-red-600" />
              <span className="text-slate-600">cap:</span>
              <span className="text-slate-900 font-semibold">${launchDefaults.today_committed_usd || 0}</span>
              <span className="text-slate-400">/</span>
              <span className="text-red-700 font-semibold">${launchDefaults.daily_spend_cap_usd}</span>
            </div>
          )}
          <button
            onClick={() => load(true)}
            disabled={busy}
            data-testid="refresh-scouting"
            className="flex items-center gap-2 bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-60 rounded-md px-3 py-1.5 text-sm"
            title={cacheInfo?.cached ? `Cached ${cacheInfo.age_min}min ago — click to regenerate (takes 45-60s)` : "Regenerate (takes 45-60s)"}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
            {busy ? "Scouting… (45-60s)" : (cacheInfo?.cached ? `Refresh · cached ${cacheInfo.age_min}m` : "Refresh")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="scouting-grid">
        {items.map((item, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-md p-4 hover:border-[#3f3f46] transition-colors">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <div className="text-xs text-slate-500 font-mono capitalize mb-1">{item.suggested_vertical}</div>
                <div className="text-base font-semibold tracking-tight text-slate-900" data-testid={`topic-${i}`}>{item.topic}</div>
              </div>
              <div className="flex items-center gap-1.5 bg-blue-500/10 border border-blue-500/20 rounded px-2 py-1">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                <div className="text-[10px] font-mono text-blue-700">{item.trend_score}</div>
              </div>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed mb-3">{item.rationale}</p>
            {item.ai_sge_angle && (
              <div className="mb-3 bg-indigo-50 border border-indigo-200 rounded px-2 py-1.5">
                <div className="flex items-center gap-1 mb-0.5">
                  <Sparkles className="w-3 h-3 text-indigo-600" />
                  <span className="text-[10px] font-mono uppercase tracking-wider text-indigo-700">AI-SGE angle</span>
                  {item.competition_score !== undefined && (
                    <span className="ml-auto flex items-center gap-1 text-[10px] font-mono text-slate-500">
                      <Target className="w-3 h-3" />
                      competition {item.competition_score}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-indigo-900 leading-snug">{item.ai_sge_angle}</div>
              </div>
            )}
            <div className="space-y-1 mb-3">
              {(item.suggested_hooks || []).map((h, hi) => (
                <div key={hi} className="text-[11px] text-slate-600 bg-white border border-slate-200 rounded px-2 py-1">
                  {h}
                </div>
              ))}
            </div>
            <div className="flex gap-1.5">
              <button
                onClick={() => pickIdea(item)}
                data-testid={`use-idea-${i}`}
                className="flex-1 flex items-center justify-center gap-1.5 bg-white border border-slate-300 text-slate-700 rounded-md px-2 py-1.5 text-xs font-medium hover:border-blue-400 hover:text-blue-600 transition-colors"
              >
                Use <ArrowUpRight className="w-3 h-3" />
              </button>
              <button
                onClick={() => reserveFirst(item, i)}
                disabled={reserving !== null}
                data-testid={`reserve-first-${i}`}
                title="Generate full brief + auto-promote top keyword — review in Campaign Builder"
                className="flex-1 flex items-center justify-center gap-1.5 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-md px-2 py-1.5 text-xs font-medium hover:from-indigo-700 hover:to-blue-700 transition-colors disabled:opacity-50"
              >
                {reserving?.idx === i && reserving?.mode === "reserve"
                  ? <><Loader2 className="w-3 h-3 animate-spin" /> Reserving…</>
                  : <><Rocket className="w-3 h-3" /> Reserve</>}
              </button>
              <button
                onClick={() => setConfirmLaunch({ item, idx: i })}
                disabled={reserving !== null || !launchDefaults?.configured}
                data-testid={`auto-launch-${i}`}
                title={launchDefaults?.configured
                  ? `⚠ Spends real budget — creates a PAUSED Meta campaign at $${launchDefaults.daily_budget_usd}/day`
                  : "Auto-launch defaults not configured yet — ask an admin to set them in Connections"}
                className="flex-1 flex items-center justify-center gap-1.5 bg-red-600 border-2 border-red-700 text-white rounded-md px-2 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-red-700 hover:border-red-800 transition-colors disabled:opacity-30 disabled:cursor-not-allowed shadow-sm shadow-red-500/30 ring-1 ring-red-400/40"
              >
                {reserving?.idx === i && reserving?.mode === "launch"
                  ? <><Loader2 className="w-3 h-3 animate-spin" /> Launching…</>
                  : <><Zap className="w-3 h-3" /> Auto-launch</>}
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && !busy && (
          <div className="col-span-2 text-center py-12 text-slate-500 text-sm">
            <div className="mb-2">No ideas yet.</div>
            <button onClick={() => load(true)} className="text-blue-600 hover:underline text-sm">
              Generate fresh ideas (takes 45-60s — Claude + Google Trends)
            </button>
          </div>
        )}
        {items.length === 0 && busy && (
          <div className="col-span-2 text-center py-12 text-slate-500 text-sm">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3 text-blue-500" />
            <div>Scouting trending topics… this takes 45-60 seconds.</div>
            <div className="text-[11px] text-slate-400 mt-1">Claude analyzes 8 verticals, then Google Trends re-ranks by real search velocity.</div>
          </div>
        )}
      </div>

      {!launchDefaults?.configured && items.length > 0 && (
        <div className="mt-4 text-[11px] font-mono text-slate-500 italic text-center">
          <Zap className="w-3 h-3 inline mr-1" />
          Auto-launch disabled — {isAdmin
            ? <>configure launch defaults on <a href="/connections" className="text-blue-600 hover:underline">Connections</a> to enable zero-click ship.</>
            : <>ask an admin to configure launch defaults in Connections.</>}
        </div>
      )}

      {confirmLaunch && (
        <div
          className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4"
          data-testid="confirm-launch-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmLaunch(null); }}
        >
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-5 border-2 border-red-500">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 bg-red-600 rounded-md flex items-center justify-center">
                <Zap className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="text-sm font-bold text-red-700 uppercase tracking-wider">Auto-launch — spends budget</div>
                <div className="text-[11px] text-slate-500 font-mono">Zero-click Matrix ship</div>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded p-2.5 mb-3 text-[11px] text-red-800 leading-relaxed">
              <strong>Heads up:</strong> This creates a real Meta campaign immediately — no Campaign Builder review. It starts <strong>paused</strong>, but once you flip it to Active in Ads Manager you're burning <strong>${launchDefaults?.daily_budget_usd || 20}/day</strong>. Use only for high-confidence AI-SGE plays.
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded p-2.5 mb-3 text-[11px] font-mono space-y-1">
              <div><span className="text-slate-500">topic: </span><span className="text-slate-900">{confirmLaunch.item.topic}</span></div>
              <div><span className="text-slate-500">vertical: </span><span className="text-slate-900">{confirmLaunch.item.suggested_vertical}</span></div>
              <div><span className="text-slate-500">daily budget: </span><span className="text-emerald-700 font-bold">${launchDefaults?.daily_budget_usd || 20}/day</span></div>
              <div><span className="text-slate-500">ad account: </span><span className="text-slate-900">{launchDefaults?.ad_account_id}</span></div>
              <div><span className="text-slate-500">status on create: </span><span className="text-amber-700 font-bold">paused</span></div>
              {launchDefaults?.daily_spend_cap_usd > 0 && (
                <div className="pt-1 border-t border-slate-200 mt-1">
                  <span className="text-slate-500">today's cap: </span>
                  <span className="text-slate-900">${launchDefaults.today_committed_usd || 0} + ${launchDefaults.daily_budget_usd} = ${(launchDefaults.today_committed_usd || 0) + launchDefaults.daily_budget_usd}</span>
                  <span className="text-slate-400"> / </span>
                  <span className="text-red-700 font-semibold">${launchDefaults.daily_spend_cap_usd}</span>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmLaunch(null)}
                data-testid="cancel-launch"
                className="flex-1 bg-white border border-slate-300 text-slate-700 rounded-md px-3 py-2 text-xs font-medium hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={autoLaunch}
                data-testid="confirm-launch"
                className="flex-1 flex items-center justify-center gap-1.5 bg-red-600 border-2 border-red-700 text-white rounded-md px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-red-700"
              >
                <Zap className="w-3 h-3" /> I understand, launch
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
